// Units of measure: types, a `useUnits()` hook and a picker every module can drop into a form.
//
// A quantity typed next to a free-text "pcs" is a guess about what it counts. The list is small
// and the same for every company of the account, so offer it — and let a line that was entered in
// boxes work out its pieces without a round trip, from the same conversions the server applies.
import { useApi } from "@keel/core";
import { Select } from "../Select";
import { Field } from "../ui";

export interface Unit {
  id: string;
  code: string;
  name: string;
  /** Printed after a quantity ("12 kg"); null when the code is as short as it gets. */
  symbol: string | null;
  /** How finely this unit is counted: 0 for pieces, 3 for kilograms. */
  decimalPlaces: number;
  isActive: boolean;
}

/** "1 `fromCode` = `factor` `toCode`", stored once and read in either direction. */
export interface UnitConversion {
  id: string;
  fromUnitId: string;
  fromCode: string;
  toUnitId: string;
  toCode: string;
  factor: number;
}

/** What a quantity is written with: the symbol when there is one, the code otherwise. */
export const unitLabel = (u: Pick<Unit, "code" | "symbol">) => u.symbol ?? u.code;

/**
 * Every unit, retired ones included, plus a lookup by code. Pickers filter the retired ones out
 * themselves; a line that still carries one has to be able to name it.
 */
export function useUnits() {
  const q = useApi<Unit[]>("/setup/units?includeInactive=true");
  const units = q.data ?? [];
  return {
    units,
    active: units.filter((u) => u.isActive),
    loading: q.isLoading,
    byCode: (code: string | null | undefined) => units.find((u) => u.code === code),
  };
}

export function useUnitConversions() {
  const q = useApi<UnitConversion[]>("/setup/units/conversions");
  return { conversions: q.data ?? [], loading: q.isLoading };
}

/**
 * `qty` in `from`, expressed in `to`: one stored conversion, or the inverse of one — the same rule
 * as `GET /setup/units/convert`, so what a form previews is what the server will store. Null when
 * nothing says how the two relate.
 */
export function convertQty(
  qty: number,
  from: string,
  to: string,
  conversions: UnitConversion[],
): number | null {
  if (from === to) return qty;
  const direct = conversions.find((c) => c.fromCode === from && c.toCode === to);
  if (direct) return qty * direct.factor;
  const inverse = conversions.find((c) => c.fromCode === to && c.toCode === from);
  return inverse ? qty / inverse.factor : null;
}

export interface UnitSelectProps {
  /** The unit's code, which is what cards and lines store. */
  value: string | null | undefined;
  onChange: (code: string) => void;
  /** Allow no unit at all, with this label. */
  emptyLabel?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * The bare control. Retired units are left out, except the one already chosen: a product that
 * was counted in crates still says so after crates are retired, greyed out rather than vanished.
 */
export function UnitSelect({
  value,
  onChange,
  emptyLabel,
  disabled,
  placeholder = "Choose a unit…",
}: UnitSelectProps) {
  const { units } = useUnits();
  const offered = units.filter((u) => u.isActive || u.code === value);
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {offered.map((u) => (
        <option key={u.code} value={u.code} disabled={!u.isActive}>
          {u.code} · {u.name}
          {u.isActive ? "" : " (retired)"}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label, and a hint that says how finely the chosen unit is counted. */
export function UnitField({ label, hint, ...rest }: UnitSelectProps & { label: string; hint?: string }) {
  const { byCode } = useUnits();
  const chosen = byCode(rest.value);
  const precision =
    chosen &&
    (chosen.decimalPlaces === 0
      ? `Whole ${chosen.name.toLowerCase()} only`
      : `Up to ${chosen.decimalPlaces} decimal${chosen.decimalPlaces === 1 ? "" : "s"}`);
  return (
    <Field label={label} hint={hint ?? precision}>
      <UnitSelect {...rest} />
    </Field>
  );
}
